import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { Workflow } from '../../store/project/projectSlice';
import { WindowRun } from '../../services/executionApi';
import WorkflowTree, { HistoryScope } from './WorkflowTree';
import ScheduleCalendar from './ScheduleCalendar';
import WindowRunHistory from './WindowRunHistory';
import RunTimeline from './RunTimeline';
import { useTranslation } from 'react-i18next';

interface ExecutionScreenProps {
  /** 대시보드에서 넘어올 때 미리 고를 워크플로 */
  initialWorkflowId?: string | null;
}

const ExecutionScreen: React.FC<ExecutionScreenProps> = ({ initialWorkflowId }) => {
  const { t } = useTranslation();
  const selectedProjectId = useSelector((state: RootState) => state.project.selectedProjectId);
  const projects = useSelector((state: RootState) => state.project.projects);
  
  // 현재 선택된 프로젝트
  const selectedProject = projects.find(p => p.id === selectedProjectId);
  
  // 선택된 워크플로 상태
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  // 실행이 끝나면 값을 올려 이력·일정이 다시 읽게 한다
  const [reloadKey, setReloadKey] = useState(0);
  const handleRunFinished = useCallback(() => setReloadKey((k) => k + 1), []);

  const workflows = useSelector((state: RootState) => state.project.workflows);
  const projectWorkflows = useMemo(
    () => workflows.filter((w) => w.projectId === selectedProjectId),
    [workflows, selectedProjectId],
  );
  // 그래프가 읽은 구간·실행 — 아래 실행 이력 목록이 그대로 쓴다(따로 읽으면 둘이 어긋난다)
  const [windowData, setWindowData] = useState<{ runs: WindowRun[]; range: { start: Date; end: Date } | null; loading: boolean }>(
    { runs: [], range: null, loading: true },
  );
  /** 오른쪽 트리에서 '이력' 을 누른 범위 — 워크플로 하나이거나 폴더·프로젝트 전체 (null 이면 프로젝트 전체) */
  const [focus, setFocus] = useState<HistoryScope | null>(null);

  const graphWorkflows = useMemo(() => {
    if (!focus) return projectWorkflows;
    const ids = new Set(focus.ids);
    return projectWorkflows.filter((w) => ids.has(w.id));
  }, [projectWorkflows, focus]);

  /**
   * 오른쪽 트리의 '이력' — 왼쪽 그래프·실행 목록을 그 범위로 좁힌다.
   * **기간은 건드리지 않는다** — 그래프에 설정된 구간을 그대로 쓴다(사용자가 고른 구간이 기준이다).
   */
  const handleShowHistory = useCallback((scope: HistoryScope) => {
    setFocus(scope);
    if (scope.ids.length === 1) {
      const w = workflows.find((x) => x.id === scope.ids[0]);
      if (w) setSelectedWorkflow(w);
    }
  }, [workflows]);
  useEffect(() => {
    if (!initialWorkflowId) return;
    const w = workflows.find((x) => x.id === initialWorkflowId);
    if (w) setSelectedWorkflow(w);
  }, [initialWorkflowId, workflows]);
  
  return (
    <div className="flex flex-col h-full w-full bg-gray-50 p-6 overflow-auto">
      <div className="text-2xl font-bold mb-2">{t('execution:screen.title', { project: selectedProject?.name })}</div>
      <div className="text-xs text-gray-500 mb-1">
        {t('execution:screen.intro')}
      </div>
      {selectedProject?.description && (
        <div className="text-gray-500 mb-6">{selectedProject.description}</div>
      )}
      
      <div className="flex flex-col lg:flex-row gap-6">
        {/* 왼쪽: 그래프와 그 구간의 실행 이력 — 같은 데이터를 쓰므로 둘이 어긋나지 않는다 */}
        <div className="lg:w-2/3 min-w-0 space-y-6">
          {focus && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-800">
              <span className="truncate">
                <b>{focus.label}</b>{t('execution:screen.focus', { n: focus.ids.length })}
              </span>
              <button
                type="button"
                className="ml-auto px-2 py-0.5 rounded border border-blue-300 bg-white hover:bg-blue-100"
                onClick={() => setFocus(null)}
              >
                {t('execution:screen.showAll')}
              </button>
            </div>
          )}
          <RunTimeline
            workflows={graphWorkflows}
            reloadKey={reloadKey}
            onSelectWorkflow={setSelectedWorkflow}
            onWindowData={setWindowData}
          />
          <WindowRunHistory
            runs={windowData.runs}
            range={windowData.range}
            loading={windowData.loading}
            workflows={projectWorkflows}
            selectedWorkflowId={selectedWorkflow?.id ?? null}
            onSelectWorkflow={setSelectedWorkflow}
          />
        </div>

        {/* 오른쪽: 워크플로 목록(선택·즉시실행·이력 보기) + 그 워크플로의 실행 일정 */}
        <div className="lg:w-1/3 min-w-0 space-y-6">
          <WorkflowTree
            selectedWorkflowId={selectedWorkflow?.id ?? null}
            onSelectWorkflow={setSelectedWorkflow}
            onRunFinished={handleRunFinished}
            onShowHistory={handleShowHistory}
            focusIds={focus?.ids ?? null}
          />
          <ScheduleCalendar selectedWorkflow={selectedWorkflow} reloadKey={reloadKey} />
        </div>
      </div>
    </div>
  );
};

export default ExecutionScreen; 